import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Link,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  QrCode,
  AccountBalance,
  Business,
  CheckCircle,
  Warning,
  Info
} from '@mui/icons-material';

const QRPaymentSetupDialog = ({ open, onClose, onSave, currentConfig, paymentMethod }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [config, setConfig] = useState({
    provider: 'paynow', // paynow, qrph, promptpay, etc.
    enabled: true,
    testMode: true,
    paynow: {
      uen: '',
      businessName: '',
      merchantCategoryCode: '5814', // Food & Beverage
      connected: false
    },
    qrph: {
      merchantId: '',
      merchantName: '',
      acquirerBankCode: '',
      connected: false
    },
    promptpay: {
      merchantId: '',
      nationalId: '',
      mobileNumber: '',
      connected: false
    },
    displaySettings: {
      showQRCode: true,
      qrCodeSize: 'medium',
      instructions: 'Scan QR code to pay',
      timeout: 300 // 5 minutes
    },
    ...currentConfig
  });

  const qrProviders = [
    {
      id: 'paynow',
      name: 'PayNow (Singapore)',
      description: 'Singapore\'s national QR payment system',
      countries: ['Singapore'],
      requirements: ['UEN', 'Business Registration']
    },
    {
      id: 'qrph',
      name: 'QR Ph (Philippines)', 
      description: 'Philippines national QR payment standard',
      countries: ['Philippines'],
      requirements: ['BSP Registration', 'Merchant ID']
    },
    {
      id: 'promptpay',
      name: 'PromptPay (Thailand)',
      description: 'Thailand\'s instant payment service',
      countries: ['Thailand'],
      requirements: ['National ID', 'Mobile Number']
    },
    {
      id: 'duitnow',
      name: 'DuitNow QR (Malaysia)',
      description: 'Malaysia\'s interoperable QR payment',
      countries: ['Malaysia'],
      requirements: ['Business Registration', 'Bank Account']
    }
  ];

  useEffect(() => {
    if (currentConfig && Object.keys(currentConfig).length > 0) {
      setConfig(prevConfig => ({ ...prevConfig, ...currentConfig }));
    }
  }, [currentConfig]);

  const handleProviderChange = (providerId) => {
    setConfig(prev => ({ ...prev, provider: providerId }));
  };

  const handleConfigChange = (section, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleTestConnection = async (provider) => {
    // Simulate connection test
    setTimeout(() => {
      setConfig(prev => ({
        ...prev,
        [provider]: {
          ...prev[provider],
          connected: true
        }
      }));
    }, 2000);
  };

  const handleSave = () => {
    onSave({
      ...config,
      setupCompleted: config[config.provider].connected,
      lastUpdated: new Date().toISOString()
    });
  };

  const setupSteps = [
    'Choose QR Provider',
    'Business Registration',
    'Technical Setup',
    'Testing & Verification'
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <QrCode />
          <Typography variant="h6">QR Payment Setup</Typography>
          {paymentMethod && (
            <Chip label={paymentMethod.name} color="primary" size="small" />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Provider" />
          <Tab label="Configuration" />
          <Tab label="Display Settings" />
          <Tab label="Testing" />
        </Tabs>

        {/* Tab 1: Provider Selection */}
        {activeTab === 0 && (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Select your QR payment provider based on your business location and requirements.
            </Typography>

            <Grid container spacing={2}>
              {qrProviders.map((provider) => (
                <Grid item xs={12} md={6} key={provider.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      border: config.provider === provider.id ? 2 : 1,
                      borderColor: config.provider === provider.id ? 'primary.main' : 'grey.300',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                    onClick={() => handleProviderChange(provider.id)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6">{provider.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {provider.description}
                          </Typography>
                        </Box>
                        {config.provider === provider.id && (
                          <CheckCircle color="primary" />
                        )}
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Supported Countries:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          {provider.countries.map((country) => (
                            <Chip key={country} label={country} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Requirements:
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {provider.requirements.map((req, index) => (
                            <Typography key={index} variant="caption" sx={{ display: 'block', ml: 1 }}>
                              • {req}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Tab 2: Configuration */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {qrProviders.find(p => p.id === config.provider)?.name} Setup
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={config.testMode}
                  onChange={(e) => setConfig(prev => ({ ...prev, testMode: e.target.checked }))}
                />
              }
              label="Test Mode (Use sandbox environment)"
              sx={{ mb: 3 }}
            />

            {/* PayNow Configuration */}
            {config.provider === 'paynow' && (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Business UEN"
                  value={config.paynow.uen}
                  onChange={(e) => handleConfigChange('paynow', 'uen', e.target.value)}
                  placeholder="200123456A"
                  helperText="Your business Unique Entity Number from ACRA"
                />
                
                <TextField
                  fullWidth
                  label="Business Name"
                  value={config.paynow.businessName}
                  onChange={(e) => handleConfigChange('paynow', 'businessName', e.target.value)}
                  placeholder="Your registered business name"
                  helperText="Must match your business registration"
                />
                
                <FormControl fullWidth>
                  <InputLabel>Merchant Category Code</InputLabel>
                  <Select
                    value={config.paynow.merchantCategoryCode}
                    onChange={(e) => handleConfigChange('paynow', 'merchantCategoryCode', e.target.value)}
                    label="Merchant Category Code"
                  >
                    <MenuItem value="5814">Food & Beverage</MenuItem>
                    <MenuItem value="5311">Department Stores</MenuItem>
                    <MenuItem value="5999">General Retail</MenuItem>
                    <MenuItem value="7372">Services</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={() => handleTestConnection('paynow')}
                  disabled={!config.paynow.uen || !config.paynow.businessName}
                  startIcon={config.paynow.connected ? <CheckCircle /> : <AccountBalance />}
                  color={config.paynow.connected ? 'success' : 'primary'}
                >
                  {config.paynow.connected ? 'Connected' : 'Verify Business Registration'}
                </Button>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Setup Requirements:</strong>
                    <br />• Valid Singapore business registration (UEN)
                    <br />• Bank account linked to PayNow
                    <br />• MAS compliance for payment services
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Similar configurations for other providers... */}
          </Box>
        )}

        {/* Tab 3: Display Settings */}
        {activeTab === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>QR Code Display Settings</Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.displaySettings.showQRCode}
                      onChange={(e) => handleConfigChange('displaySettings', 'showQRCode', e.target.checked)}
                    />
                  }
                  label="Show QR Code in POS"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>QR Code Size</InputLabel>
                  <Select
                    value={config.displaySettings.qrCodeSize}
                    onChange={(e) => handleConfigChange('displaySettings', 'qrCodeSize', e.target.value)}
                    label="QR Code Size"
                  >
                    <MenuItem value="small">Small (150px)</MenuItem>
                    <MenuItem value="medium">Medium (200px)</MenuItem>
                    <MenuItem value="large">Large (300px)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Payment Timeout (seconds)"
                  type="number"
                  value={config.displaySettings.timeout}
                  onChange={(e) => handleConfigChange('displaySettings', 'timeout', parseInt(e.target.value))}
                  inputProps={{ min: 60, max: 600 }}
                  helperText="How long QR code remains valid"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Instructions"
                  multiline
                  rows={3}
                  value={config.displaySettings.instructions}
                  onChange={(e) => handleConfigChange('displaySettings', 'instructions', e.target.value)}
                  placeholder="Instructions shown to customers..."
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 4: Testing */}
        {activeTab === 3 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Test Your QR Payment Setup</Typography>
            
            <Stepper orientation="vertical">
              {setupSteps.map((step, index) => (
                <Step key={step} active={index <= activeTab}>
                  <StepLabel>{step}</StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary">
                      {index === 0 && "Select and configure your QR payment provider"}
                      {index === 1 && "Verify business registration and credentials"}
                      {index === 2 && "Configure technical integration settings"}
                      {index === 3 && "Test with sample transactions"}
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            <Alert severity="warning" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Testing Notes:</strong>
                <br />• Use test mode for initial setup
                <br />• Verify with small amounts first
                <br />• Check with your payment provider for test procedures
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!config[config.provider].connected}
        >
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QRPaymentSetupDialog;